import { add, dot, length, scale, sub, type Vec3 } from '@rcs/domain';

/**
 * コース曲線。パラメーター `t` は区間番号＋区間内の位置（0 〜 `segmentCount`）で、
 * 制御点 `i` を `t = i` で通る。物理・描画はこの曲線を直接使わず、
 * 弧長サンプリング（{@link sampleByArcLength}）の結果を使う（ADR-0006）。
 */
export interface TrackCurve {
  /** 周回コースか */
  readonly closed: boolean;
  /** 区間数（開いたコースは制御点数 − 1、周回コースは制御点数） */
  readonly segmentCount: number;
  /** 位置 P(t) [m] */
  position(t: number): Vec3;
  /** 1 階微分 dP/dt */
  derivative(t: number): Vec3;
  /** 2 階微分 d²P/dt² */
  secondDerivative(t: number): Vec3;
}

/** {@link createCatmullRom} のオプション。 */
export interface CatmullRomOptions {
  /** 周回コースにする（既定 `false`）。制御点は 3 点以上必要 */
  readonly closed?: boolean;
  /** 節点間隔の指数。0.5 = centripetal（既定）、0 = uniform、1 = chordal */
  readonly alpha?: number;
}

interface Cubic {
  readonly c0: Vec3;
  readonly c1: Vec3;
  readonly c2: Vec3;
  readonly c3: Vec3;
}

function pointAt(points: readonly Vec3[], index: number): Vec3 {
  const point = points[index];
  if (point === undefined) throw new RangeError(`制御点 ${String(index)} がありません`);
  return point;
}

/**
 * centripetal Catmull-Rom スプラインを作る（ADR-0006）。
 * 各区間を 3 次多項式に変換する（Yuksel らの定式化）ため、微分は解析的に求まる。
 * 曲線は C1 連続（接線が連続）で、曲率は制御点で不連続になりうる。
 *
 * 開いたコースの両端は、端の制御点を折り返した仮想点で延長する。
 *
 * @throws {RangeError} 制御点が少ない・座標が有限でない・隣り合う制御点が同一の場合
 */
export function createCatmullRom(
  points: readonly Vec3[],
  options: CatmullRomOptions = {},
): TrackCurve {
  const closed = options.closed ?? false;
  const alpha = options.alpha ?? 0.5;
  const count = points.length;
  const minimum = closed ? 3 : 2;
  if (count < minimum) {
    throw new RangeError(`制御点が足りません（${String(minimum)} 点以上が必要）: ${String(count)}`);
  }
  for (const [index, point] of points.entries()) {
    if (!point.every(Number.isFinite)) {
      throw new RangeError(`制御点 ${String(index)} の座標が有限ではありません`);
    }
  }
  const segmentCount = closed ? count : count - 1;
  for (let i = 0; i < segmentCount; i += 1) {
    if (length(sub(pointAt(points, (i + 1) % count), pointAt(points, i))) === 0) {
      throw new RangeError(`隣り合う制御点 ${String(i)} と ${String((i + 1) % count)} が同一です`);
    }
  }

  const neighbor = (index: number): Vec3 => {
    if (closed) return pointAt(points, ((index % count) + count) % count);
    if (index < 0) return sub(scale(pointAt(points, 0), 2), pointAt(points, 1));
    if (index >= count) {
      return sub(scale(pointAt(points, count - 1), 2), pointAt(points, count - 2));
    }
    return pointAt(points, index);
  };

  const segments: Cubic[] = [];
  for (let i = 0; i < segmentCount; i += 1) {
    const p0 = neighbor(i - 1);
    const p1 = neighbor(i);
    const p2 = neighbor(i + 1);
    const p3 = neighbor(i + 2);
    // 端の仮想点が隣の点と重なる場合に備え、節点間隔の下限を設ける
    const knot = (a: Vec3, b: Vec3): number => Math.max(Math.pow(length(sub(b, a)), alpha), 1e-9);
    const dt0 = knot(p0, p1);
    const dt1 = knot(p1, p2);
    const dt2 = knot(p2, p3);
    // 節点間隔 dt1 で正規化した端点の接線（区間内の u ∈ [0, 1] に対する値）
    const m1 = add(
      sub(scale(sub(p1, p0), 1 / dt0), scale(sub(p2, p0), 1 / (dt0 + dt1))),
      scale(sub(p2, p1), 1 / dt1),
    );
    const m2 = add(
      sub(scale(sub(p2, p1), 1 / dt1), scale(sub(p3, p1), 1 / (dt1 + dt2))),
      scale(sub(p3, p2), 1 / dt2),
    );
    const t1 = scale(m1, dt1);
    const t2 = scale(m2, dt1);
    segments.push({
      c0: p1,
      c1: t1,
      c2: sub(sub(add(scale(p1, -3), scale(p2, 3)), scale(t1, 2)), t2),
      c3: add(add(sub(scale(p1, 2), scale(p2, 2)), t1), t2),
    });
  }

  const locate = (t: number): { segment: Cubic; u: number } => {
    const local = closed
      ? ((t % segmentCount) + segmentCount) % segmentCount
      : Math.min(Math.max(t, 0), segmentCount);
    const index = Math.min(Math.floor(local), segmentCount - 1);
    const segment = segments[index];
    if (segment === undefined) throw new RangeError(`区間 ${String(index)} がありません`);
    return { segment, u: local - index };
  };

  return {
    closed,
    segmentCount,
    position(t) {
      const { segment: s, u } = locate(t);
      return add(s.c0, scale(add(s.c1, scale(add(s.c2, scale(s.c3, u)), u)), u));
    },
    derivative(t) {
      const { segment: s, u } = locate(t);
      return add(s.c1, scale(add(scale(s.c2, 2), scale(s.c3, 3 * u)), u));
    },
    secondDerivative(t) {
      const { segment: s, u } = locate(t);
      return add(scale(s.c2, 2), scale(s.c3, 6 * u));
    },
  };
}

/** 曲線の接線方向の単位ベクトルと曲率ベクトル（dT/ds）を返す。 */
export function frenetAt(curve: TrackCurve, t: number): { tangent: Vec3; curvature: Vec3 } {
  const d1 = curve.derivative(t);
  const speed = length(d1);
  if (!(speed > 1e-12)) {
    throw new RangeError(`t = ${String(t)} で曲線が退化しています（接線の長さ 0）`);
  }
  const tangent = scale(d1, 1 / speed);
  const d2 = curve.secondDerivative(t);
  const normalPart = sub(d2, scale(tangent, dot(d2, tangent)));
  return { tangent, curvature: scale(normalPart, 1 / (speed * speed)) };
}
